/**
 * Access process training (13 Oct): queries and mutations.
 *
 * Roles and the roster read Ivanti on the server (cached there); the
 * prompt reads only the database, so the chat card and the wizard can ask
 * cheaply. A completion or an answer re-reads what it changed.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  acceptAccessTrainingCorrection as sdkAcceptCorrection,
  completeAccessTraining as sdkComplete,
  confirmAccessRosterEntry as sdkConfirm,
  type CorrectionResolveResponse,
  dismissAccessTrainingCorrection as sdkDismissCorrection,
  dismissAccessTrainingPrompt as sdkDismissPrompt,
  getAccessTrainingProgress as sdkProgress,
  getAccessTrainingPrompt as sdkPrompt,
  getMyAccessRoster as sdkMyRoster,
  getMyTrainingRoles as sdkRoles,
  type MyRosterResponse,
  type RoleConfirmationRequest,
  type RoleConfirmationView,
  type TrainingCompletion,
  type TrainingCompletionRequest,
  type TrainingProgressResponse,
  type TrainingPrompt,
  type TrainingRolesResponse,
} from "@/lib/api";
import { logger } from "@/lib/logger";

export const trainingKeys = {
  all: ["access-training"] as const,
  roles: () => [...trainingKeys.all, "roles"] as const,
  prompt: () => [...trainingKeys.all, "prompt"] as const,
  roster: () => [...trainingKeys.all, "roster"] as const,
  progress: () => [...trainingKeys.all, "progress"] as const,
};

/** The progress workbook: a plain same-origin link, so the browser downloads it. */
export const ACCESS_TRAINING_EXPORT_URL = "/api/admin/access-training/export";

export function useTrainingRoles() {
  return useQuery({
    queryKey: trainingKeys.roles(),
    queryFn: async (): Promise<TrainingRolesResponse> => {
      const { data } = await sdkRoles({ throwOnError: true });
      return data;
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useTrainingPrompt(enabled = true) {
  return useQuery({
    queryKey: trainingKeys.prompt(),
    queryFn: async (): Promise<TrainingPrompt> => {
      const { data } = await sdkPrompt({ throwOnError: true });
      return data;
    },
    enabled,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    // A prompt is a nicety: never retry, never shout.
    retry: false,
  });
}

export function useDismissTrainingPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await sdkDismissPrompt({ throwOnError: true });
    },
    onMutate: () => {
      // Hide it at once; the server remembers it for good.
      queryClient.setQueryData<TrainingPrompt>(trainingKeys.prompt(), (old) =>
        old ? { ...old, show_prompt: false, show_refresher: true, dismissed: true } : old,
      );
    },
    onError: (error) => logger.warn("[training] dismiss failed", error),
    onSettled: () => queryClient.invalidateQueries({ queryKey: trainingKeys.prompt() }),
  });
}

export function useCompleteTraining() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: TrainingCompletionRequest): Promise<TrainingCompletion> => {
      logger.debug("[training] complete", body.role);
      const { data } = await sdkComplete({ body, throwOnError: true });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trainingKeys.roles() });
      void queryClient.invalidateQueries({ queryKey: trainingKeys.prompt() });
    },
  });
}

export function useMyRoster(enabled = true) {
  return useQuery({
    queryKey: trainingKeys.roster(),
    queryFn: async (): Promise<MyRosterResponse> => {
      const { data } = await sdkMyRoster({ throwOnError: true });
      return data;
    },
    enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useConfirmRoster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RoleConfirmationRequest): Promise<RoleConfirmationView> => {
      const { data } = await sdkConfirm({ body, throwOnError: true });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trainingKeys.roster() }),
  });
}

export function useTrainingProgress() {
  return useQuery({
    queryKey: trainingKeys.progress(),
    queryFn: async (): Promise<TrainingProgressResponse> => {
      const { data } = await sdkProgress({ throwOnError: true });
      return data;
    },
    staleTime: 60_000,
  });
}

export function useResolveCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      accept,
    }: {
      id: number;
      accept: boolean;
    }): Promise<CorrectionResolveResponse> => {
      const call = accept ? sdkAcceptCorrection : sdkDismissCorrection;
      const { data } = await call({ path: { correction_id: id }, throwOnError: true });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trainingKeys.progress() }),
  });
}
